import React, { useState, useEffect } from 'react';
import { AnalysisDatum } from '../models/analysis-datum';
import RawMaterialSearch from './RawMaterialSearch';
import { CrudFunctions } from '../utilities/crud-functions';
import { MessageBoxReply } from '../models/message-box-reply';
import { EditType, MessageBoxReplyType } from '../models/enums';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';

import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import Guid from 'devextreme/core/guid';

import { ReactGrid, Id, CellChange, TextCell, Column, Row, CellStyle, Range, DefaultCellTypes } from "@silevis/reactgrid";

import "@silevis/reactgrid/styles.css";

const ANALYSIS_DATUM_ID_COLUMN_IDX = 0;
const EDIT_TYPE_COLUMN_ID = "editType";
const RAW_MATERIAL_LOOKUP_COLUMN_ID = "rawMaterialLookup";
const UNSAVED_CHANGES_WARNING = 100;
const CREATE_NEW_ANALYSIS_DATA_WARNING = 110;

const initColumns: Column[] = [
    { columnId: "analysisDatumId", width: -20 },
    { columnId: EDIT_TYPE_COLUMN_ID, width: -20 },
    { columnId: "elution", width: 70 },
    { columnId: "rawMaterialName", width: 180, resizable: true },
    { columnId: "rawMaterialDescription", width: 400, resizable: true },
    { columnId: RAW_MATERIAL_LOOKUP_COLUMN_ID, width: 35 },
    { columnId: "result", width: 125, resizable: true },
    { columnId: "uncertainty", width: 125, resizable: true },
    { columnId: "retentionTime", width: 125, resizable: true },
];

const headerRow: Row = {
    rowId: "header",
    cells: [
        { type: "header", text: "", className: "reactgrid-hidden-column" },
        { type: "header", text: "", className: "" },
        { type: "header", text: "Elution", className: "reactgrid-header-cell" },
        { type: "header", text: "Raw Material Name", className: "reactgrid-header-cell" },
        { type: "header", text: "Raw Material Description", className: "reactgrid-header-cell" },
        { type: "header", text: "", className: "reactgrid-header-cell" },
        { type: "header", text: "Result", className: "reactgrid-header-cell" },
        { type: "header", text: "Uncertainty", className: "reactgrid-header-cell" },
        { type: "header", text: "Retention Time", className: "reactgrid-header-cell" },
    ]
};

let key = 1;

export interface Props {
    benchSheetId?: string;
    batchId?: string;
    isReadOnly: boolean;
    datagridHeight?: string;
    paging?: boolean;
    pageSize?: number;
    title?: string;
    hideElutionColumn?: boolean;
    hideRetentionTimeColumn?: boolean;
    onClose?: () => void;
}

export default function AnalysisData(props: Props) {
    const [rows, setRows] =
        useState<Row[]>();

    const [columns, setColumns] =
        useState<Column[]>(initColumns);

    const [analysisData, setAnalysisData] =
        useState<AnalysisDatum[]>();

    const [selectedAnalysisDatumId, setSelectedAnalysisDatumId] =
        useState<string>();

    const [rawMaterialSearchDialogVisible, setRawMaterialSearchDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    useEffect(() => {
        getRows();
    }, [analysisData]);

    return (
        <div>
            <div className="pw-comp-row pw-margin-top-05">
                <div className="title-bar-spacer" />
                <div className="save-toolbar pw-margin-right-05">
                    <Button text='Create Analysis Data' icon="detailslayout" type='success'
                        className="pw-button" height='1.8rem'
                        onClick={onCreateAnalysisDataClick} />

                    <Button text="Add Row" icon="plus" type="success"
                        className="pw-button" height='1.8rem'
                        hint="add row"
                        onClick={onAddRowClick} />

                    <Button id="saveButton" text="Save" icon="save" type="success"
                        className="pw-button" height='1.8rem'
                        hint="save changes"
                        onClick={onSaveClick} />

                    <Button id="closeButton" text="Close" icon="close" type="normal"
                        className="pw-button" height='1.8rem'
                        hint="close dialog"
                        onClick={onCloseClick} />
                </div>
            </div>

            <div className="pw-margin-top-1">
                <ReactGrid
                    enableFillHandle={true}
                    enableRangeSelection={true}
                    rows={rows ? rows : []} columns={columns}
                    onSelectionChanging={onSelectionChanging}
                    onColumnResized={handleColumnResize}
                    onCellsChanged={handleChanges} />
            </div>

            {rawMaterialSearchDialogVisible &&
                <DialogPopup visible={rawMaterialSearchDialogVisible}
                    onHiding={hideRawMaterialSearchDialog}
                    title="Raw Material Search"
                    width={'55em'}
                    height={'40em'}>
                    <RawMaterialSearch key={`rms${key}`}
                        onCancel={hideRawMaterialSearchDialog}
                        onApplySelection={onApplyRawMaterialSelection} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata}
                    onReply={onGlobalDialogReply} onClose={onGlobalDialogClose} />
            }

        </div>
    );

    function handleColumnResize(ci: Id, width: number) {
        if (!columns)
            return;

        setColumns((prevColumns) => {
            const columnIndex = prevColumns.findIndex(el => el.columnId === ci);
            const resizedColumn = prevColumns[columnIndex];
            const updatedColumn = { ...resizedColumn, width };
            prevColumns[columnIndex] = updatedColumn;
            return [...prevColumns];
        });
    }

    function onAddRowClick() {
        let elutionNumber = 1;

        if (analysisData)
            elutionNumber = analysisData.length + 1;

        let editedAnalysisData = [...analysisData];
        let newAnalysisDatum: AnalysisDatum = { analysisDatumId: new Guid().toString(), elution: elutionNumber, editType: EditType.Insert };
        editedAnalysisData.push(newAnalysisDatum);

        setAnalysisData(editedAnalysisData);
    }

    function onSelectionChanging(selectedRanges: Range[]): boolean {
        //console.log("onSelectionChanging");
        //console.log(selectedRanges);

        if (selectedRanges && selectedRanges.length > 0 &&
            selectedRanges[0].columns && selectedRanges[0].columns.length > 0 &&
            selectedRanges[0].rows && selectedRanges[0].rows.length > 0) {

            const rowId = selectedRanges[0].rows[0].rowId;

            //console.log("rowId:");
            //console.log(rowId);

            const selectedAnalysisDatumIdCell = selectedRanges[0].first.row.cells[ANALYSIS_DATUM_ID_COLUMN_IDX] as any;
            const selectedAnalysisDatumId = selectedAnalysisDatumIdCell.text;

            //console.log("solventId:");
            //console.log(solventId);

            setSelectedAnalysisDatumId(selectedAnalysisDatumId);

            if (selectedRanges[0].columns[0].columnId === RAW_MATERIAL_LOOKUP_COLUMN_ID) {
                setRawMaterialSearchDialogVisible(true);
            }
        }

        return true;
    }

    function handleChanges(changes: CellChange<TextCell>[]) {
        setAnalysisData((prevAnalysisData) => applyChangesToAnalysisData(changes, prevAnalysisData));
    };

    function applyChangesToAnalysisData(changes: CellChange<TextCell>[], prevAnalysisData: AnalysisDatum[]): AnalysisDatum[] {
        //console.log("changes:");
        //console.log(changes);

        changes.forEach((change) => {
            const analysisDatumIndex = change.rowId;
            const fieldName = change.columnId;

            prevAnalysisData[analysisDatumIndex][fieldName] = change.newCell.text;

            if (prevAnalysisData[analysisDatumIndex].editType !== EditType.Insert)
                prevAnalysisData[analysisDatumIndex].editType = EditType.Update;
        });

        return [...prevAnalysisData];
    };


    function onSaveClick() {
        saveAnalysisData();
    }

    function onCloseClick() {
        const anyNonSavedChanges = analysisData.some(a => a.editType !== EditType.None);

        if (anyNonSavedChanges) {
            showPromptMessage(ModalMessageType.PromptYesNo,
                "Unsaved Items",
                "You have unsaved changes. Do you want to close anyway?",
                UNSAVED_CHANGES_WARNING);

            return;
        }

        closeDialog();
    }

    async function closeDialog() {
        if (props.onClose)
            props.onClose();
    }

    function onCreateAnalysisDataClick(e: any) {
        warnCreateAnalysisData();
    }

    function onApplyRawMaterialSelection(item: any) {
        //console.log("onApplyRawMaterialSelection");
        //console.log(item);

        if (!selectedAnalysisDatumId)
            return;

        let editedAnalysisData = [...analysisData];

        let matchingAnalysisData = editedAnalysisData.find(s => s.analysisDatumId === selectedAnalysisDatumId);

        if (matchingAnalysisData) {
            if (matchingAnalysisData.editType !== EditType.Insert)
                matchingAnalysisData.editType = EditType.Update;

            matchingAnalysisData.rawMaterialId = item.rawMaterialId ? item.rawMaterialId : "";
            matchingAnalysisData.rawMaterialName = item.name ? item.name : "";
            matchingAnalysisData.rawMaterialDescription = item.description ? item.description : "";

        }

        setAnalysisData(editedAnalysisData);
        setRawMaterialSearchDialogVisible(false);
    }

    /*
    async function addAnalysisDataRow(rawMaterial: RawMaterial) {
        if (rawMaterial && rawMaterial.rawMaterialId) {
            let newAnalysisDatum: AnalysisDatum = {
                elution: 0,
                rawMaterialId: rawMaterial.rawMaterialId,
                rawMaterialName: rawMaterial.name,
                rawMaterialDescription: rawMaterial.description,
                editType: EditType.Insert
            };

            let editedAnalysisData = [...analysisData];

            editedAnalysisData.push(newAnalysisDatum);

            editedAnalysisData.sort((a, b) => a.elution - b.elution);

            setAnalysisData(editedAnalysisData);
            setRawMaterialSearchDialogVisible(false);
        }
    }
    */


    //TODO:
    /*
    function duplicateAnalysisDataRow() {
        if (selectedAnalysisData) {
            let editedAnalysisData = [...analysisData];
            let newAnalyisDatum = { ...selectedAnalysisData };

            newAnalyisDatum.editType = EditType.Insert;

            editedAnalysisData.push(newAnalyisDatum);

            editedAnalysisData.sort((a, b) => a.elution - b.elution);

            setAnalysisData(editedAnalysisData);
        }
    }
    */


    /*
    function onAnalysisDataRowRemoving(e: any) {
        if (e.row && e.row.data && e.row.data.analysisDatumId) {
            let editedAnalysisData = [...analysisData];

            let matchingAnalysisDatum = editedAnalysisData.find(d => d.analysisDatumId === e.row.data.analysisDatumId);

            if (matchingAnalysisDatum) {
                //if element was just added but not saved, just get rid of it
                if (matchingAnalysisDatum.editType === EditType.Insert) {
                    const idx = editedAnalysisData.findIndex(d => d.analysisDatumId === e.row.data.analysisDatumId);

                    if (typeof idx === 'number')
                        editedAnalysisData.splice(idx, 1);
                }
                else {
                    matchingAnalysisDatum.editType = EditType.Delete;
                }

                setAnalysisData(editedAnalysisData);
            }
        }
    }
    */

    async function saveAnalysisData() {
        if (!analysisData)
            return;

        let preparedAnalysisData = [...analysisData];

        preparedAnalysisData.forEach(d => {
            d.benchSheetId = props.benchSheetId;
        });

        console.log("preparedAnalysisData...");
        console.log(preparedAnalysisData);

        await updateAnalysisData(preparedAnalysisData)
            .then((data) => {
                getAnalysisData();

            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating Analysis Data");
            });
    }

    async function updateAnalysisData(preparedAnalysisData: AnalysisDatum[]) {
        if (preparedAnalysisData) {
            await CrudFunctions.putData("benchsheets/analysisdata", preparedAnalysisData)
                .then(() => {
                    getAnalysisData();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Updating Analysis Data");
                });
        }
    }

    async function createAnalysisData() {
        await CrudFunctions.postData(`benchsheets/createanalysisdataforbenchsheet/${props.benchSheetId}/${props.batchId}`, null)
            .then(() => {
                getAnalysisData();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Creating Analysis Data");
            });
    }

    /*
    function onAnalysisDataRemoving(e: any) {
        if (e.data && e.data.analysisDatumId) {
            if (e.data)
                e.data.editType = EditType.Delete;

            
        }
    }

    function onSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setSelectedAnalysisData(e.selectedRowsData[0]);
        }
    }
    */

    function warnCreateAnalysisData() {
        //we do not want to overwrite any analysis data
        if (analysisData && analysisData.length > 0) {
            showPromptMessage(ModalMessageType.PromptYesNo,
                "Analysis Data Change",
                "This action will delete any existing analysis data, and create a new set. Do you want to proceed?",
                CREATE_NEW_ANALYSIS_DATA_WARNING);

            return;
        }
        else
            createAnalysisData();
    }

    /*
    function confirmCreateAnalysisData() {
        createAnalysisData();
    }

    
    async function addAnalysisDatum(analysisDatum: AnalysisDatum) {
        if (analysisDatum) {
            analysisDatum.benchSheetId = props.benchSheetId;

            await insertAnalysisDatum(analysisDatum)
                .then(() => {
                    getAnalysisData();
                })
                .catch((response: ResponseError) => {
                    XXX.publish(ModalSubscriberTopic, { modalType: ApiResponseErrorModal, responseError: response });
                });
        }
    }
    

    async function onAnalysisDataValidating(e: any) {
        if (e.oldData && e.oldData.editType !== EditType.Insert)
            e.newData.editType = EditType.Update;
    }
    */

    // CRUD functions

    async function getAnalysisData() {
        if (props.benchSheetId)
            await CrudFunctions.getData(`benchsheets/analysisdatabybenchsheetid/${props.benchSheetId}`)
                .then(data => {
                    setAnalysisData(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Analysis Data");
                });
    }

    async function getAllData() {
        await getAnalysisData();
    }

    // show/hide

    function hideRawMaterialSearchDialog() {
        setRawMaterialSearchDialogVisible(false);
    }

    function getRows() {
        let createdRows: Row[] = [];

        if (analysisData) {
            createdRows = [
                headerRow,
                ...analysisData.map<Row<DefaultCellTypes>>((analysisDatum, idx) => ({
                    rowId: idx,
                    cells: [
                        { type: "text", text: analysisDatum.analysisDatumId ? analysisDatum.analysisDatumId : "", nonEditable: true, className: "reactgrid-hidden-column" },
                        { type: "text", text: "", nonEditable: true, className: getEditTypeTemplate(analysisDatum) },
                        { type: "text", text: analysisDatum.elution ? analysisDatum.elution.toString() : "", className: "reactgrid-cell pw-bold reactgrid-horizontal-center" },
                        { type: "text", text: analysisDatum.rawMaterialName ? analysisDatum.rawMaterialName : "", className: "reactgrid-cell" },
                        { type: "text", text: analysisDatum.rawMaterialDescription ? analysisDatum.rawMaterialDescription : "", className: 'reactgrid-cell' },
                        { type: "text", text: " ...", nonEditable: true, style: chemicalCellStyle(), className: 'reactgrid-button' },
                        { type: "text", text: analysisDatum.result ? analysisDatum.result.toString() : "", className: "reactgrid-cell" },
                        { type: "text", text: analysisDatum.uncertainty ? analysisDatum.uncertainty.toString() : "", className: "reactgrid-cell" },
                        { type: "text", text: analysisDatum.retentionTime ? analysisDatum.retentionTime.toString() : "", className: "reactgrid-cell" },
                    ]
                }))
            ];
        }
        else {
            let newAnalysisData: AnalysisDatum[] = [];
            newAnalysisData.push({ analysisDatumId: new Guid().toString(), elution: 1, editType: EditType.Insert });

            setAnalysisData(newAnalysisData);
        }

        setRows(createdRows);
    }

    //render

    function chemicalCellStyle(): CellStyle {
        let cellStyle: CellStyle = {
            background: "#efefef",
            color: "blue"
        };

        return cellStyle;
    }

    function getEditTypeTemplate(analysisDatum: AnalysisDatum) {
        switch (analysisDatum.editType) {
            case EditType.None:
                return "";
            case EditType.Insert:
                return "reactgrid-add-icon";
            case EditType.Update:
                return "reactgrid-edit-icon";
            case EditType.Delete:
                return "reactgrid-delete-icon";
        }
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function showPromptMessage(mode: ModalMessageType, title: string, message: string, code: number) {
        let mm: ModalMetadata = {
            error: null,
            mode: mode,
            title: title,
            message: message,
            code: code
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function onGlobalDialogReply(reply: MessageBoxReply) {
        if (reply.code === CREATE_NEW_ANALYSIS_DATA_WARNING) {
            clearGlobalDialog();

            if (reply.reply !== MessageBoxReplyType.Yes)
                return;

            createAnalysisData();
        }
        if (reply.code === UNSAVED_CHANGES_WARNING) {
            clearGlobalDialog();

            if (reply.reply !== MessageBoxReplyType.Yes)
                return;

            closeDialog();
        }
    }

    function onGlobalDialogClose() {
        clearGlobalDialog();
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}
